/* Precision 3D: presentation adapter for Dawn's existing cart requests.
 * Never intercept fetch or submit a product form a second time.
 */
if (!customElements.get('p3d-cart-popup')) {
  customElements.define('p3d-cart-popup', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.supported = typeof this.dialog?.showModal === 'function';
      this.querySelectorAll('[data-popup-close]').forEach(button => button.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]')]
          .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
        if (!focusable.length) { event.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      });
      this.dialog.addEventListener('close', () => this.restorePage());
      this.addEventListener('submit', event => {
        if (!event.target.matches('[data-popup-add-form]')) return;
        event.preventDefault();
        this.addRecommendation(event.target);
      });
      this.addEventListener('change', event => {
        if (event.target.matches('select[name="id"]')) this.updateVariant(event.target);
      });
      this.generation = 0;
    }

    get nativeCart() { return document.querySelector('cart-notification, cart-drawer'); }
    captureBulkAddition(items, container) {
      return Object.entries(items).filter(([id, quantity]) => {
        const input = container.querySelector(`input[data-index="${id}"]`);
        return Number(quantity) > Number(input?.dataset.cartQuantity || 0);
      }).map(([id]) => String(id));
    }
    confirmBulkAddition(response, variantIds, activeElement) {
      if (!this.supported || response.errors || response.status || !variantIds.length) return;
      const items = response.items?.filter(item => variantIds.includes(String(item.variant_id || item.id)));
      if (!items?.length) return;
      this.setActiveElement(activeElement);
      const modal = activeElement?.closest('modal-dialog[open]');
      if (modal) { this.setActiveElement(modal.openedBy); modal.hide(); }
      return this.renderContents({ ...response, items });
    }
    setActiveElement(element) { this.activeElement = element; }
    getSectionsToRender() {
      const sections = this.nativeCart?.getSectionsToRender() || [{ id: 'cart-icon-bubble' }];
      return [...sections, { id: 'p3d-popup-content' }];
    }
    async getSnapshot(response) {
      let markup = response.sections?.['p3d-popup-content'];
      if (!markup) {
        const url = new URL(routes.cart_url, location.origin);
        url.searchParams.set('section_id', 'p3d-popup-content');
        const result = await fetch(url, { signal: AbortSignal.timeout(12000) });
        if (!result.ok) throw new Error('Cart confirmation could not be loaded.');
        markup = await result.text();
      }
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const snapshot = doc.querySelector('[data-popup-snapshot]');
      if (!snapshot?.querySelector('[data-popup-lines]') || !snapshot.querySelector('[data-popup-totals]')) {
        throw new Error('Cart confirmation could not be loaded.');
      }
      return snapshot;
    }
    syncNativeSections(response) {
      // Keep the normal drawer, notes, discounts and header cart ready without opening it.
      const targets = [
        { id: 'cart-drawer', selector: '#CartDrawer', target: '#CartDrawer' },
        { id: 'cart-icon-bubble', selector: '.shopify-section', target: '#cart-icon-bubble' },
      ];
      for (const section of targets) {
        const markup = response.sections?.[section.id];
        const target = document.querySelector(section.target);
        if (!markup || !target) continue;
        const source = new DOMParser().parseFromString(markup, 'text/html').querySelector(section.selector);
        if (source) target.innerHTML = source.innerHTML;
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.classList.remove('is-empty');
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      }
    }
    applyTotals(snapshot) {
      this.querySelector('[data-popup-total]').replaceChildren(snapshot.querySelector('[data-popup-totals]').content.cloneNode(true));
      // A bundled header section may be null even though the add succeeded.
      // Keep its count accurate from the same Shopify-rendered cart snapshot.
      const count = Number(snapshot.dataset.count);
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (cartLink && Number.isInteger(count) && count >= 0) {
        let bubble = cartLink.querySelector('.cart-count-bubble');
        if (!count) bubble?.remove();
        else {
          if (!bubble) { bubble = document.createElement('div'); bubble.className = 'cart-count-bubble'; cartLink.append(bubble); }
          const visible = document.createElement('span');
          visible.setAttribute('aria-hidden', 'true');
          visible.textContent = count < 100 ? String(count) : '';
          const label = document.createElement('span');
          label.className = 'visually-hidden';
          label.textContent = `${count} ${count === 1 ? 'item' : 'items'} in cart`;
          bubble.replaceChildren(visible, label);
        }
      }
    }
    applyRepeatOffers(snapshot) {
      const container = this.querySelector('[data-popup-repeat]');
      if (!container) return;
      const candidates = snapshot.querySelector('[data-popup-repeat-offers]')?.content.querySelectorAll('[data-popup-repeat-offer]') || [];
      const seen = new Set();
      const offers = [...candidates].filter(offer => {
        if (!this.addedVariantIds?.has(offer.dataset.variantId) || seen.has(offer.dataset.variantId)) return false;
        seen.add(offer.dataset.variantId);
        return true;
      });
      container.replaceChildren(...offers.map(offer => offer.cloneNode(true)));
      container.hidden = !offers.length;
    }
    lineIdentity(variantId, properties, planId) {
      const entries = Array.isArray(properties) ? properties : Object.entries(properties || {});
      return JSON.stringify([String(variantId), entries.filter(([, value]) => value !== null && value !== '').sort(([a], [b]) => a.localeCompare(b)), String(planId || '')]);
    }
    findCartLines(lines, item) {
      const exact = lines.filter(line => line.dataset.lineKey === item.key);
      if (exact.length) return exact;
      // Shopify can change line keys when automatic discounts start applying.
      // Use variant + properties + selling plan, never variant alone.
      const identity = this.lineIdentity(item.variant_id || item.id, item.properties, item.selling_plan_allocation?.selling_plan?.id || item.selling_plan);
      return lines.filter(line => this.lineIdentity(line.dataset.variantId, JSON.parse(line.dataset.properties || '{}'), line.dataset.sellingPlan) === identity);
    }
    async renderContents(response) {
      const generation = ++this.generation;
      try {
        const snapshot = await this.getSnapshot(response);
        if (generation !== this.generation) return;
        const added = response.items || [response];
        const lines = [...snapshot.querySelector('[data-popup-lines]').content.querySelectorAll('[data-line-key]')];
        const selected = [...new Set(added.flatMap(item => this.findCartLines(lines, item)))];
        if (!selected.length) throw new Error('Added item could not be confirmed.');
        this.addedProductIds = new Set(selected.map(line => line.dataset.productId));
        this.addedVariantIds = new Set(selected.map(line => line.dataset.variantId));
        this.cartProductIds = new Set(lines.map(line => line.dataset.productId));
        this.eligible = selected.some(line => line.dataset.eligible === 'true');
        this.recommendationProductId = selected.find(line => line.dataset.eligible === 'true')?.dataset.productId;
        this.querySelector('[data-popup-added]').replaceChildren(...selected.map(line => line.cloneNode(true)));
        this.querySelector('[data-popup-grid]').replaceChildren();
        this.querySelector('[data-popup-recommendations]').hidden = true;
        this.querySelector('[data-popup-status]').textContent = '';
        this.applyTotals(snapshot);
        this.applyRepeatOffers(snapshot);
        this.syncNativeSections(response);
        this.open();
        // Recommendation failures never turn a successful cart addition into a failed addition.
        this.loadRecommendations(snapshot, generation).catch(() => {
          if (generation === this.generation && this.dialog.open) this.querySelector('[data-popup-status]').textContent = 'Your item is in the cart. Recommendations are unavailable right now.';
        });
      } catch (error) {
        // The add already succeeded. Never retry it; fall back to the original UI/cart.
        this.close();
        try {
          if (!this.nativeCart) throw error;
          this.nativeCart.setActiveElement(this.activeElement);
          this.nativeCart.renderContents(response);
        } catch (_) { location.assign(routes.cart_url); }
      }
    }
    async loadRecommendations(snapshot, generation) {
      if (!this.eligible) return;
      if (this.dataset.source === 'shopify') {
        const url = new URL(this.dataset.recommendationsUrl, location.origin);
        url.searchParams.set('section_id', 'p3d-popup-content');
        url.searchParams.set('product_id', this.recommendationProductId);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        const result = await fetch(url, { signal: AbortSignal.timeout(10000) });
        if (!result.ok) throw new Error('Recommendations unavailable');
        snapshot = new DOMParser().parseFromString(await result.text(), 'text/html');
      }
      if (generation !== this.generation || !this.dialog.open) return;
      const candidates = snapshot.querySelector('[data-popup-candidates]')?.content.querySelectorAll('[data-popup-product]') || [];
      const seen = new Set();
      const selected = [];
      for (const card of candidates) {
        const id = card.dataset.productId;
        if (seen.has(id) || (this.dataset.excludeAdded === 'true' && this.addedProductIds.has(id)) ||
            (this.dataset.excludeCart === 'true' && this.cartProductIds.has(id))) continue;
        selected.push(card.cloneNode(true));
        seen.add(id);
        if (selected.length >= Number(this.dataset.limit || 3)) break;
      }
      this.querySelector('[data-popup-grid]').replaceChildren(...selected);
      this.querySelector('[data-popup-recommendations]').hidden = !selected.length;
      this.querySelectorAll('select[name="id"]').forEach(select => this.updateVariant(select));
    }
    updateVariant(select) {
      const option = select.selectedOptions[0];
      if (!option) return;
      const card = select.closest('[data-popup-product]');
      card.querySelector('[data-variant-price]').textContent = option.dataset.price;
      for (const field of ['compare', 'saving']) {
        const element = card.querySelector(`[data-variant-${field}]`);
        element.textContent = option.dataset[field];
        element.hidden = !option.dataset[field];
      }
      const image = card.querySelector('img');
      if (image) {
        image.dataset.defaultImage ||= image.getAttribute('src');
        image.src = option.dataset.image || image.dataset.defaultImage;
        image.removeAttribute('srcset');
      }
      const quantity = card.querySelector('[name="quantity"]');
      const inCart = Number(option.dataset.cartQuantity || 0);
      const increment = Number(option.dataset.step) || 1;
      const min = inCart ? increment : Number(option.dataset.min) || 1;
      quantity.min = String(min);
      quantity.step = String(increment);
      quantity.value = String(min);
      const remaining = option.dataset.max ? Math.max(0, Number(option.dataset.max) - inCart) : null;
      if (remaining !== null) quantity.max = String(remaining);
      else quantity.removeAttribute('max');
      const button = card.querySelector('[type="submit"]');
      button.disabled = option.disabled || (remaining !== null && remaining < min) || select.form.dataset.completed === 'true' || select.form.dataset.cooldown === 'true';
      if (select.form.dataset.completed === 'true') return;
      button.textContent = button.disabled ? 'Limit reached' : button.dataset.addLabel;
      card.querySelector('[data-product-message]').textContent = '';
    }
    async addRecommendation(form) {
      if (this.adding || form.dataset.completed === 'true' || form.dataset.cooldown === 'true' || !form.reportValidity()) return;
      const button = form.querySelector('[type="submit"]');
      if (button.disabled) return;
      this.adding = true;
      const message = form.querySelector('[data-product-message]');
      message.textContent = '';
      const controls = [...this.querySelectorAll('[data-popup-add-form] button, [data-popup-add-form] select, [data-popup-add-form] input')];
      const disabled = controls.map(control => control.disabled);
      // Read before disabling, so the selected variant and quantity are included.
      const body = new FormData(form);
      body.append('sections', this.getSectionsToRender().map(section => section.id).join(','));
      body.append('sections_url', location.pathname);
      controls.forEach(control => { control.disabled = true; });
      button.textContent = 'Adding…';
      let accepted = false;
      let uncertain = false;
      let response;
      let updatedSnapshot;
      const repeat = form.hasAttribute('data-popup-repeat-form');
      try {
        const result = await fetch(routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        response = await result.json();
        if (!result.ok || response.status) {
          // A stock-limit response can still partially add quantity. Do not automatically retry.
          uncertain = true;
          throw new Error(typeof response.description === 'string' ? response.description : 'This option cannot be added. Check your cart before trying again.');
        }
        accepted = true;
        button.textContent = 'Added!';
        const snapshot = await this.getSnapshot(response);
        updatedSnapshot = snapshot;
        this.applyTotals(snapshot);
        this.syncNativeSections(response);
        const cartLines = [...snapshot.querySelector('[data-popup-lines]').content.querySelectorAll('[data-line-key]')];
        this.cartProductIds = new Set(cartLines.map(line => line.dataset.productId));
        // Preserve the main confirmation while updating its authoritative cart quantity/pricing.
        this.querySelectorAll('[data-popup-added] [data-line-key]').forEach(current => {
          const updated = this.findCartLines(cartLines, {
            key: current.dataset.lineKey, variant_id: current.dataset.variantId,
            properties: JSON.parse(current.dataset.properties || '{}'), selling_plan: current.dataset.sellingPlan,
          })[0];
          if (updated) current.replaceWith(updated.cloneNode(true));
        });
        const option = form.querySelector('select')?.selectedOptions[0];
        if (option) option.dataset.cartQuantity = String(Number(option.dataset.cartQuantity || 0) + Number(body.get('quantity')));
        message.textContent = repeat ? 'One more added. Your updated total is shown below.' : 'Added to your order.';
        this.querySelector('[data-popup-status]').textContent = `Added to your order. Cart now contains ${snapshot.dataset.count} items.`;
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'p3d-cart-popup', productVariantId: body.get('id'), cartData: response });
      } catch (error) {
        uncertain = true;
        button.textContent = accepted ? 'Added!' : 'Check cart';
        message.textContent = accepted
          ? 'Added successfully, but totals could not refresh. View Cart for your current total.'
          : `${error.message || 'Connection interrupted.'} View Cart to confirm the quantity before adding again.`;
        this.querySelector('[data-popup-total]').textContent = 'View Cart for your current subtotal.';
        if (response) this.syncNativeSections(response);
        if (accepted && typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'p3d-cart-popup', productVariantId: body.get('id'), cartData: response });
      } finally {
        this.adding = false;
        controls.forEach((control, index) => { control.disabled = disabled[index]; });
        if (uncertain) {
          // Avoid accidental duplicate orders after an ambiguous/partial server response.
          form.dataset.completed = 'true';
          button.disabled = true;
        } else {
          form.dataset.cooldown = 'true';
          button.disabled = true;
          setTimeout(() => {
            if (!form.isConnected) return;
            delete form.dataset.cooldown;
            if (repeat) {
              // Re-evaluate from Shopify's new cart state. Do not keep suggesting an already-met offer.
              if (form.contains(document.activeElement)) this.querySelector('#P3DPopupHeading').focus();
              this.applyRepeatOffers(updatedSnapshot);
            } else if (this.dataset.excludeCart === 'true') {
              form.dataset.completed = 'true';
              button.textContent = 'In your cart';
            } else this.updateVariant(form.querySelector('select'));
          }, 1200);
          if (!repeat) this.applyRepeatOffers(updatedSnapshot);
        }
      }
    }
    open() {
      if (this.dialog.open) return;
      const native = this.nativeCart;
      if (native?.classList.contains('active') || native?.querySelector('.active')) native.close();
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      this.scrollY = window.scrollY;
      this.oldBodyStyle = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width, overflow: document.body.style.overflow };
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.querySelector('#P3DPopupHeading').focus();
    }
    close() {
      if (!this.dialog.open) return;
      this.dialog.close();
      this.restorePage();
    }
    restorePage() {
      if (!this.oldBodyStyle) return;
      ++this.generation;
      Object.assign(document.body.style, this.oldBodyStyle);
      this.oldBodyStyle = null;
      window.scrollTo(0, this.scrollY || 0);
      const target = this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble');
      target?.focus({ preventScroll: true });
    }
  });
}
